// InvoiceListPage.jsx

import React, { useEffect, useState, useCallback } from 'react';
import { useDebounce, useHospitalDetails } from 'api/cutsom-hooks';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';

// material‐ui
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';

// icons
import MoneyOutlinedIcon from '@mui/icons-material/MoneyOutlined';
import QrCodeScannerOutlinedIcon from '@mui/icons-material/QrCodeScannerOutlined';

// project‐import
import GenericTable from 'components/GenericTable';
import ReportCard from 'components/cards/statistics/ReportCard';
import useAuth from 'hooks/useAuth';

// utils/apiHelper
import { fetchPharmacyInvoices, fetchLabInvoices, fetchOpdInvoices, fetchUserCollectionTotals, fetchStores, fetchUsers } from 'utils/apiHelper';
import { EditOutlined, PlusOutlined, FormOutlined, CheckCircleOutlined, SyncOutlined } from '@ant-design/icons';
import HistoryOutlined from '@ant-design/icons/HistoryOutlined';
import CancellationLedgerModal from 'components/CancellationLedgerModal';
import InvoiceViewer from 'components/InvoiceViewer';
import { useApp, useAppDispatch } from 'contexts/AppContext';
import { usePageSizeContext } from 'contexts/PageSizeContext';
import LinkIcon from '@mui/icons-material/Link';
import AbhaHipLinker from 'sections/apps/doctor/AbhaHipLinker';


// Tab index <-> ?activeTab= value (Pharmacy is the default, with no param)
const TAB_KEYS = ['pharmacy', 'lab', 'opd'];
const tabIndexFromKey = (key) => Math.max(TAB_KEYS.indexOf(key), 0);

// Must match the backend's INVOICE_REF_PREFIX (abha/utils/abha_link.py)
const invoiceCareContextRef = (invoiceNumber) => `INV-${invoiceNumber}`;

const isPatientOnly = (roles = []) => roles.length === 1 && roles[0].toLowerCase() === 'patient';
const formatPaymentMethod = (paymentMethod) => {
    if (!paymentMethod) return 'Unknown';
    if (paymentMethod.toLowerCase() === 'hybrid') return 'Cash + Online';
    return paymentMethod.charAt(0).toUpperCase() + paymentMethod.slice(1).toLowerCase();
};

export default function InvoiceListPage({ customerId: propCustomerId, hideEdit = false }) {
    const { user, roles = [], isAdmin } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const appState = useApp();
    const appDispatch = useAppDispatch();
    const selectedStore = appState.selectedStore;
    const [stores, setStores] = useState([]);
    const [selectedStoreFilter, setSelectedStoreFilter] = useState('');
    const [selectedDate, setSelectedDate] = useState('');
    const [selectedCreatedBy, setSelectedCreatedBy] = useState(null);
    const [userSearchQuery, setUserSearchQuery] = useState('');
    const [userOptions, setUserOptions] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    
    // Get customer ID from props or URL parameters for patient-specific filtering
    const customerId = propCustomerId || searchParams.get('customer');
    

    const [pharmacyInvoices, setPharmacyInvoices] = useState([]);
    const [labInvoices, setLabInvoices] = useState([]);
    const [opdInvoices, setOpdInvoices] = useState([]);
    const [totalPharmacyRecords, setTotalPharmacyRecords] = useState(0);
    const [totalLabRecords, setTotalLabRecords] = useState(0);
    const [totalOpdRecords, setTotalOpdRecords] = useState(0);
    const [selectedTab, setSelectedTab] = useState(0);
    const [pharmacyTotals, setPharmacyTotals] = useState(null);
    const [labTotals, setLabTotals] = useState(null);
    const [pendingCancellationPrint, setPendingCancellationPrint] = useState(null);
    const [cancellationInvoiceNumber, setCancellationInvoiceNumber] = useState('');
    const [paymentNotice, setPaymentNotice] = useState(null);
    const [openHipLinker, setOpenHipLinker] = useState(false);
    const [hipLinkPatient, setHipLinkPatient] = useState(null);
    const [hipLinkContexts, setHipLinkContexts] = useState([]);
    const { pageSize: preferredPageSize, hasStoredValue } = usePageSizeContext();
    
    const debouncedSearchQuery = useDebounce(searchQuery, 500, 4);
    const debouncedUserSearchQuery = useDebounce(userSearchQuery, 500, 2);
    const navigate = useNavigate();

    const fetchPharmacyInvoiceData = useCallback(
        async (nextQueryURL) => {
            setLoading(true);

            try {
                const limit = hasStoredValue ? preferredPageSize : 100;
                const query = nextQueryURL || `limit=${limit}&offset=0`;
                const response = await fetchPharmacyInvoices(
                    query, 
                    debouncedSearchQuery,
                    selectedDate ? new Date(selectedDate) : null,
                    false,
                    selectedCreatedBy?.id || null,
                    selectedStoreFilter,
                    customerId
                );
                
                if (response.results) {
                    setPharmacyInvoices(response.results);
                    setTotalPharmacyRecords(response.count || response.results.length);
                } else {
                    setPharmacyInvoices(response);
                    setTotalPharmacyRecords(response.length);
                }
            } catch (err) {
                console.error('Error fetching pharmacy invoices:', err);
                setPharmacyInvoices([]);
                setTotalPharmacyRecords(0);
            }
            setLoading(false);
        },
        [debouncedSearchQuery, selectedStoreFilter, selectedDate, selectedCreatedBy, customerId, preferredPageSize, hasStoredValue]
    );

    const fetchLabInvoiceData = useCallback(
        async (nextQueryURL) => {
            setLoading(true);
            try {
                const limit = hasStoredValue ? preferredPageSize : 100;
                const query = nextQueryURL || `limit=${limit}&offset=0`;
                const response = await fetchLabInvoices(
                    query, 
                    debouncedSearchQuery,
                    selectedDate ? new Date(selectedDate) : null,
                    false,
                    selectedCreatedBy?.id || null,
                    customerId
                );
                
                if (response.results) {
                    setLabInvoices(response.results);
                    setTotalLabRecords(response.count || response.results.length);
                } else {
                    setLabInvoices(response);
                    setTotalLabRecords(response.length);
                }
            } catch (err) {
                console.error('Error fetching lab invoices:', err);
                setLabInvoices([]);
                setTotalLabRecords(0);
            }
            setLoading(false);
        },
        [debouncedSearchQuery, selectedDate, selectedCreatedBy, customerId, preferredPageSize, hasStoredValue]
    );
    const fetchOpdInvoiceData = useCallback(
        async (nextQueryURL) => {
            setLoading(true);
            try {
                const limit = hasStoredValue ? preferredPageSize : 100;
                const query = nextQueryURL || `limit=${limit}&offset=0`;
                const response = await fetchOpdInvoices(
                    query,
                    debouncedSearchQuery,
                    selectedDate ? new Date(selectedDate) : null,
                    false,
                    selectedCreatedBy?.id || null,
                    customerId
                );

                if (response.results) {
                    setOpdInvoices(response.results);
                    setTotalOpdRecords(response.count || response.results.length);
                } else {
                    setOpdInvoices(response);
                    setTotalOpdRecords(response.length);
                }
            } catch (err) {
                console.error('Error fetching OPD invoices:', err);
                setOpdInvoices([]);
                setTotalOpdRecords(0);
            }
            setLoading(false);
        },
        [debouncedSearchQuery, selectedDate, selectedCreatedBy, customerId, preferredPageSize, hasStoredValue]
    );

    useEffect(() => {
        const tabFromUrl = tabIndexFromKey(searchParams.get('activeTab'));
        if (tabFromUrl !== selectedTab) {
            setSelectedTab(tabFromUrl);
        }
    }, [searchParams, selectedTab]);

    useEffect(() => {
        const showCancellationPrint = searchParams.get('showCancellationPrint');
        const invoiceNumber = searchParams.get('invoiceNumber');
        const type = searchParams.get('type');
        
        if (showCancellationPrint === 'true' && invoiceNumber) {
            setPendingCancellationPrint(invoiceNumber);
            
            if (TAB_KEYS.includes(type)) {
                setSelectedTab(tabIndexFromKey(type));
            } else {
                setSelectedTab(tabIndexFromKey(searchParams.get('activeTab')));
            }
            
            // Clean up URL parameters
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.delete('showCancellationPrint');
            newSearchParams.delete('invoiceNumber');
            newSearchParams.delete('cancelledItems');
            newSearchParams.delete('type');
            setSearchParams(newSearchParams, { replace: true, state: location.state });
        }
    }, [searchParams, setSearchParams, location.state]);

    const fetchDailyTotals = useCallback(async () => {
        try {
            const response = await fetchUserCollectionTotals();
            
            const totals = {
                cash_total: response.cash_collections || 0,
                online_total: response.online_collections || 0,
                total_amount: response.total_collections || 0,
                total_invoices: response.total_invoices || 0,
                gst_total: response.gst_collections || 0,
                cgst_total: response.cgst_collections || 0,
                sgst_total: response.sgst_collections || 0,
                igst_total: response.igst_collections || 0
            };
            
            setPharmacyTotals(totals);
            setLabTotals(totals);
        } catch (err) {
            console.error('Error fetching daily totals:', err);
            setPharmacyTotals(null);
            setLabTotals(null);
        }
    }, []);

    useEffect(() => {
        fetchPharmacyInvoiceData();
        fetchLabInvoiceData();
        fetchOpdInvoiceData();
    }, [debouncedSearchQuery, selectedStoreFilter, selectedDate, selectedCreatedBy, fetchPharmacyInvoiceData, fetchLabInvoiceData, fetchOpdInvoiceData]);

    // Fetch users for created_by filter
    useEffect(() => {
        const loadUsers = async () => {
            if (debouncedUserSearchQuery.length >= 2) {
                setLoadingUsers(true);
                try {
                    const response = await fetchUsers(debouncedUserSearchQuery, null, true); // true = isEmployee flag
                    setUserOptions(response?.results || []);
                } catch (error) {
                    console.error('Error fetching users:', error);
                    setUserOptions([]);
                } finally {
                    setLoadingUsers(false);
                }
            } else {
                setUserOptions([]);
            }
        };
        loadUsers();
    }, [debouncedUserSearchQuery]);

    useEffect(() => {
        if (user?.id) {
            fetchDailyTotals();
        }
    }, [user?.id, fetchDailyTotals]);

    useEffect(() => {
        const loadStores = async () => {
            try {
                const response = await fetchStores("limit=1000");
                setStores(response?.results || []);
            } catch (error) {
                console.error('Error fetching stores:', error);
                setStores([]);
            }
        };
        loadStores();
    }, []);

    const navigateToInvoiceAction = (invoice, variant, storeObject, inPlaceEdit = false) => {
        if (storeObject)
            appDispatch({ type: 'SELECT_STORE', payload: storeObject});

        const editQuery = inPlaceEdit ? '&inPlaceEdit=true' : '';
        if (variant === 'pharmacy') {
            navigate(`/invoice/pharmacy-invoice?invoiceId=${invoice.invoice_number}${editQuery}`, {
                state: { invoiceData: invoice, isEdit: true },
            })
        }
            else if (variant === 'lab') {
            navigate(`/invoice/lab-invoice?invoiceId=${invoice.invoice_number}${editQuery}`, {
                state: { invoiceData: invoice, isEdit: true },
            });
        }
    };

    const handleEdit = (invoice, variant, storeObject) => {
        if (invoice?.payment_method?.toLowerCase() === 'wallet') {
            setPaymentNotice({ invoice, variant, storeObject, action: 'cancel' });
            return;
        }
        navigateToInvoiceAction(invoice, variant, storeObject);
    };

    const handleInPlaceEdit = (invoice, variant, storeObject) => {
        setPaymentNotice({ invoice, variant, storeObject, action: 'edit' });
    };

    const handlePaymentNoticeConfirm = () => {
        if (!paymentNotice) return;
        const { invoice, variant, storeObject, action } = paymentNotice;
        setPaymentNotice(null);
        navigateToInvoiceAction(invoice, variant, storeObject, action === 'edit');
    };

    const isAccountManager = roles?.includes("superuser") || roles?.includes("account_manager") || false;
    const isWrongStore = (row, variant) => {
        return !(isAccountManager) &&
               variant === 'pharmacy' && 
               selectedStore?.id !== row?.original?.store?.id;
    }

    const handleOpenAbhaLink = (invoiceRow) => {
        const cust = invoiceRow.customer || (customerId ? { id: customerId } : {});
        setHipLinkPatient(cust);
        setHipLinkContexts([
            {
                // Namespaced: a bare invoice number has the same format as an
                // appointment ID (e.g. "…O-5") and would resolve to that visit.
                referenceNumber: invoiceCareContextRef(invoiceRow.invoice_number),
                display: `Invoice ${invoiceRow.invoice_number}`,
                hiType: 'Invoice',
            }
        ]);
        setOpenHipLinker(true);
    };

    const getColumns = (variant) => [
        {
            header: 'Invoice No.',
            accessorKey: 'invoice_number',
            enableSorting: true,
            cell: ({ getValue }) => <Typography>{getValue()}</Typography>,
        },
        {
            header: 'Customer Name',
            accessorKey: 'customer.full_name',
            enableSorting: false,
            cell: ({ getValue }) => <Typography>{getValue() || '-'}</Typography>,
        },
        {
            header: 'Total (₹)',
            accessorKey: 'total_amount',
            enableSorting: true,
            cell: ({ getValue }) => {
                const amt = parseFloat(getValue() || 0);
                return (
                    <Typography>
                        ₹{amt.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                );
            },
        },
        variant === 'opd' ? {
            // OPD bills carry no doctor details; show the consulted appointment(s)
            header: 'Appointment',
            accessorFn: (row) =>
                (row?.items?.opd_appointment || []).map((item) => item?.id).filter(Boolean).join(', ') || '-',
            enableSorting: false,
            cell: ({ getValue }) => <Typography>{getValue()}</Typography>,
        } : {
            header: 'Doctor Name',
            accessorFn: (row) => {
                try {
                  const details = typeof row?.other_details === "string"
                      ? JSON.parse(row.other_details)
                      : row?.other_details;
              
                  return details?.doctor_name ?? "-";
                } catch {
                  return "-";
                }
            },              
            enableSorting: true,
            cell: ({ getValue }) => <Typography>{getValue()}</Typography>,
        },
        {
            header: 'Status',
            accessorKey: 'status',
            enableSorting: false,
            cell: ({ getValue }) => {
                const status = getValue();
                if (!status) return <Typography>–</Typography>;
                
                // Determine chip color and label based on status
                let chipColor = 'default';
                let chipLabel = status;
                
                switch (status.toLowerCase()) {
                    case 'paid':
                    case 'completed':
                    case 'active':
                        chipColor = 'success';
                        chipLabel = 'Active';
                        break;
                    case 'pending':
                    case 'unpaid':
                        chipColor = 'warning';
                        chipLabel = 'Pending';
                        break;
                    case 'cancelled':
                    case 'canceled':
                        chipColor = 'error';
                        chipLabel = 'Cancelled';
                        break;
                    case 'partially_cancelled':
                    case 'partially cancelled':
                        chipColor = 'warning';
                        chipLabel = 'Partially Cancelled';
                        break;
                    default:
                        chipColor = 'info';
                        chipLabel = status;
                }
                
                return (
                    <Chip 
                        label={chipLabel} 
                        color={chipColor} 
                        size="small" 
                        variant="filled"
                    />
                );
            },
        },
        {
            header: 'Created At',
            accessorKey: 'created_at',
            enableSorting: true,
            cell: ({ getValue }) => {
                const date = new Date(getValue());
                return <Typography>{date.toLocaleString('en-IN')}</Typography>;
            },
        },
        {
            header: 'Actions',
            enableSorting: false,
            cell: ({ row }) => {
                const invoiceStatus = String(row.original.status || '').toLowerCase();
                const hasCancellations = ['cancelled', 'canceled', 'partially_cancelled', 'partially cancelled'].includes(invoiceStatus);
                // OPD bills are managed from the appointment; there is no OPD invoice edit screen.
                const canEditInvoice = !hideEdit && !isPatientOnly(roles) && variant !== 'opd';

                return (
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start" flexWrap="nowrap">
                    {canEditInvoice && (
                        <Tooltip title={isWrongStore(row, variant)? "Please select the correct store": "Cancel / Sale Return"}>
                            <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleEdit(row.original, variant, row?.original?.store);
                                }}
                                disabled={isWrongStore(row, variant)}
                            >
                                <EditOutlined style={{ fontSize: '1rem' }} />
                            </IconButton>
                        </Tooltip>
                    )}
                    {canEditInvoice && isAdmin && (
                        <Tooltip title={isWrongStore(row, variant)? "Please select the correct store": "In-place Edit (Super Admin)"}>
                            <IconButton
                                size="small"
                                color="warning"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleInPlaceEdit(row.original, variant, row?.original?.store);
                                }}
                                disabled={isWrongStore(row, variant)}
                            >
                                <FormOutlined style={{ fontSize: '1rem' }} />
                            </IconButton>
                        </Tooltip>
                    )}
                    <InvoiceViewer 
                        invoiceData={row.original} 
                        customerInfo={row.original.customer || {}}
                        autoTriggerCancellationPrint={pendingCancellationPrint === row.original.invoice_number}
                        onInvoiceData={() => {
                            if (pendingCancellationPrint === row.original.invoice_number) {
                                setPendingCancellationPrint(null);
                            }
                        }}
                        hospitalData={hospitalData}
                    />
                    {hasCancellations && !isPatientOnly(roles) && (
                        <Tooltip title="View Cancellation History">
                            <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setCancellationInvoiceNumber(row.original.invoice_number);
                                }}
                            >
                                <HistoryOutlined style={{ fontSize: '1rem' }} />
                            </IconButton>
                        </Tooltip>
                    )}
                    {!isPatientOnly(roles) && (() => {
                        const abhaLinkStatus = row.original.abha_link_status || (row.original.abha_linked ? 'LINKED' : 'NOT_LINKED');
                        const isLinked = abhaLinkStatus === 'LINKED';
                        const isPending = abhaLinkStatus === 'PENDING';
                        const isFailed = abhaLinkStatus === 'FAILED';

                        let abhaTooltip = "Link to ABHA / ABDM";
                        let abhaColor = "info";
                        let AbhaIcon = LinkIcon;

                        if (isLinked) {
                            abhaTooltip = "ABHA Linked";
                            abhaColor = "success";
                            AbhaIcon = CheckCircleOutlined;
                        } else if (isPending) {
                            abhaTooltip = "Linking in progress...";
                            abhaColor = "warning";
                            AbhaIcon = SyncOutlined;
                        } else if (isFailed) {
                            abhaTooltip = "Link failed. Click to retry.";
                            abhaColor = "error";
                            AbhaIcon = LinkIcon;
                        }

                        return (
                            <Tooltip title={abhaTooltip}>
                                <IconButton
                                    size="small"
                                    color={abhaColor}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenAbhaLink(row.original);
                                    }}
                                    disabled={isPending}
                                >
                                    <AbhaIcon style={{ fontSize: '1.1rem' }} />
                                </IconButton>
                            </Tooltip>
                        );
                    })()}
                </Stack>
                );
            },
        },
    ]



    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
        const newSearchParams = new URLSearchParams(searchParams);
        if (newValue > 0) {
            newSearchParams.set('activeTab', TAB_KEYS[newValue]);
        } else {
            newSearchParams.delete('activeTab');
        }
        setSearchParams(newSearchParams, { replace: true, state: location.state });
    };

    const canCreatePharmacy = isAdmin || roles.includes('pharmacy_biller');
    const canCreateLab = isAdmin || roles.includes('lab_billing');

    const hospitalData = useHospitalDetails();
    
    return (
        <Box sx={{ width: '100%', px: 2, py: 3 }}>
            <CancellationLedgerModal
                invoiceNumber={cancellationInvoiceNumber}
                open={Boolean(cancellationInvoiceNumber)}
                onClose={() => setCancellationInvoiceNumber('')}
            />
            <Dialog open={Boolean(paymentNotice)} onClose={() => setPaymentNotice(null)} maxWidth="xs" fullWidth>
                <DialogTitle>
                    {paymentNotice?.action === 'cancel' ? 'Wallet Refund' : 'Invoice Payment Method'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {paymentNotice?.action === 'cancel'
                            ? 'This bill was paid from the patient wallet. Any amount reversed by this cancellation or sale return will be credited back to the patient wallet.'
                            : `This bill was paid using ${formatPaymentMethod(paymentNotice?.invoice?.payment_method)}.`}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPaymentNotice(null)} color="inherit">Cancel</Button>
                    <Button onClick={handlePaymentNoticeConfirm} variant="contained">
                        {paymentNotice?.action === 'cancel' ? 'Continue' : 'Continue to Edit'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Tabs
                value={selectedTab}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
            >
                <Tab label={`Pharmacy (${totalPharmacyRecords})`} />
                <Tab label={`Procedure (${totalLabRecords})`} />
                <Tab label={`OPD Bills (${totalOpdRecords})`} />
            </Tabs>

            <Box mt={2}>
                {selectedTab === 0 && (
                    <>
                        {/* Hide cash collection cards when viewing patient-specific invoices */}
                        {!customerId && (
                            <Box mb={2}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} lg={6} sm={6}>
                                        <ReportCard 
                                            primary={`Rs ${pharmacyTotals ? pharmacyTotals.cash_total.toLocaleString('en-IN', { minimumFractionDigits: 2 }) : '0.00'}`} 
                                            secondary="Your Cash Collection" 
                                            color="success.main" 
                                            iconPrimary={MoneyOutlinedIcon} 
                                        />
                                    </Grid>
                                    <Grid item xs={12} lg={6} sm={6}>
                                        <ReportCard 
                                            primary={`Rs ${pharmacyTotals ? pharmacyTotals.online_total.toLocaleString('en-IN', { minimumFractionDigits: 2 }) : '0.00'}`} 
                                            secondary="Your Online Collection" 
                                            color="secondary.dark" 
                                            iconPrimary={QrCodeScannerOutlinedIcon} 
                                        />
                                    </Grid>
                                    {(pharmacyTotals?.gst_total || 0) > 0 && (
                                        <Grid item xs={12} lg={6} sm={6}>
                                            <ReportCard
                                                primary={`Rs ${pharmacyTotals.gst_total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`}
                                                secondary="GST Collected"
                                                color="warning.main"
                                                iconPrimary={MoneyOutlinedIcon}
                                            />
                                        </Grid>
                                    )}
                                </Grid>
                            </Box>
                        )}
                        <GenericTable
                            columns={getColumns('pharmacy')}
                            data={pharmacyInvoices}
                            loading={loading}
                            options={{
                                searchQuery,
                                setSearchQuery,
                                totalRecords: totalPharmacyRecords
                            }}
                            fetchData={(nextQueryURL) => fetchPharmacyInvoiceData(nextQueryURL)}
                            additionalControls={
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Select
                                        size="small"
                                        value={selectedStoreFilter}
                                        onChange={(e) => setSelectedStoreFilter(e.target.value)}
                                        displayEmpty
                                        sx={{ minWidth: 150 }}
                                        renderValue={(selected) => {
                                            if (!selected) {
                                                return <span style={{ color: '#999' }}>Store</span>;
                                            }
                                            const store = stores.find(s => s.id === selected);
                                            return store ? store.name : selected;
                                        }}
                                    >
                                        <MenuItem value="">
                                            <em>All Stores</em>
                                        </MenuItem>
                                        {stores.map((store) => (
                                            <MenuItem key={store.id} value={store.id}>
                                                {store.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <Autocomplete
                                        size="small"
                                        sx={{ minWidth: 200 }}
                                        options={userOptions}
                                        value={selectedCreatedBy}
                                        onChange={(event, newValue) => {
                                            setSelectedCreatedBy(newValue);
                                        }}
                                        onInputChange={(event, newInputValue) => {
                                            setUserSearchQuery(newInputValue);
                                        }}
                                        getOptionLabel={(option) => {
                                            if (typeof option === 'string') return option;
                                            return option?.full_name || '';
                                        }}
                                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                        loading={loadingUsers}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder="Created By"
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                            />
                                        )}
                                        renderOption={(props, option) => (
                                            <Box component="li" {...props} key={option.id}>
                                                {option.full_name} ({option.id})
                                            </Box>
                                        )}
                                        noOptionsText={debouncedUserSearchQuery.length < 2 ? "Type at least 2 characters" : "No users found"}
                                    />
                                    <TextField
                                        size="small"
                                        type="date"
                                        label="Date"
                                        value={selectedDate}
                                        onChange={(e) => setSelectedDate(e.target.value)}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        sx={{ minWidth: 150 }}
                                    />
                                    {!customerId && canCreatePharmacy && (
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            startIcon={<PlusOutlined />}
                                            onClick={() => navigate("/invoice/pharmacy-invoice")}
                                        >
                                            Pharmacy Invoice
                                        </Button>
                                    )}
                                </Stack>
                            }
                            variant="invoices"
                        />
                    </>
                )}

                {selectedTab === 1 && (
                    <>
                        {/* Hide cash collection cards when viewing patient-specific invoices */}
                        {!customerId && (
                            <Box mb={2}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} lg={6} sm={6}>
                                        <ReportCard 
                                            primary={`₹ ${labTotals ? labTotals.cash_total.toLocaleString('en-IN', { minimumFractionDigits: 2 }) : '0.00'}`} 
                                            secondary="Your Cash Collection" 
                                            color="success.main" 
                                            iconPrimary={MoneyOutlinedIcon} 
                                        />
                                    </Grid>
                                    <Grid item xs={12} lg={6} sm={6}>
                                        <ReportCard 
                                            primary={`₹ ${labTotals ? labTotals.online_total.toLocaleString('en-IN', { minimumFractionDigits: 2 }) : '0.00'}`} 
                                            secondary="Your Online Collection" 
                                            color="secondary.dark" 
                                            iconPrimary={QrCodeScannerOutlinedIcon} 
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        )}
                        <GenericTable
                            columns={getColumns('lab')}
                            data={labInvoices}
                            loading={loading}
                            options={{
                                searchQuery,
                                setSearchQuery,
                                totalRecords: totalLabRecords
                            }}
                            additionalControls={
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Autocomplete
                                        size="small"
                                        sx={{ minWidth: 200 }}
                                        options={userOptions}
                                        value={selectedCreatedBy}
                                        onChange={(event, newValue) => {
                                            setSelectedCreatedBy(newValue);
                                        }}
                                        onInputChange={(event, newInputValue) => {
                                            setUserSearchQuery(newInputValue);
                                        }}
                                        getOptionLabel={(option) => {
                                            if (typeof option === 'string') return option;
                                            return option?.full_name || '';
                                        }}
                                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                        loading={loadingUsers}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder="Created By"
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                            />
                                        )}
                                        renderOption={(props, option) => (
                                            <Box component="li" {...props} key={option.id}>
                                                {option.full_name} ({option.id})
                                            </Box>
                                        )}
                                        noOptionsText={debouncedUserSearchQuery.length < 2 ? "Type at least 2 characters" : "No users found"}
                                    />
                                    <TextField
                                        size="small"
                                        type="date"
                                        label="Date"
                                        value={selectedDate}
                                        onChange={(e) => setSelectedDate(e.target.value)}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        sx={{ minWidth: 150 }}
                                    />
                                    {!customerId && canCreateLab && (
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            startIcon={<PlusOutlined />}
                                            onClick={() => navigate("/invoice/lab-invoice")}
                                        >
                                            Lab Invoice
                                        </Button>
                                    )}
                                </Stack>
                            }
                            fetchData={(nextQueryURL) => fetchLabInvoiceData(nextQueryURL)}
                            variant="invoices"
                        />
                    </>
                )}

                {selectedTab === 2 && (
                    <>
                        {/* Hide cash collection cards when viewing patient-specific invoices */}
                        {!customerId && (
                            <Box mb={2}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} lg={6} sm={6}>
                                        <ReportCard
                                            primary={`₹ ${labTotals ? labTotals.cash_total.toLocaleString('en-IN', { minimumFractionDigits: 2 }) : '0.00'}`}
                                            secondary="Your Cash Collection"
                                            color="success.main"
                                            iconPrimary={MoneyOutlinedIcon}
                                        />
                                    </Grid>
                                    <Grid item xs={12} lg={6} sm={6}>
                                        <ReportCard
                                            primary={`₹ ${labTotals ? labTotals.online_total.toLocaleString('en-IN', { minimumFractionDigits: 2 }) : '0.00'}`}
                                            secondary="Your Online Collection"
                                            color="secondary.dark"
                                            iconPrimary={QrCodeScannerOutlinedIcon}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        )}
                        <GenericTable
                            columns={getColumns('opd')}
                            data={opdInvoices}
                            loading={loading}
                            options={{
                                searchQuery,
                                setSearchQuery,
                                totalRecords: totalOpdRecords
                            }}
                            additionalControls={
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Autocomplete
                                        size="small"
                                        sx={{ minWidth: 200 }}
                                        options={userOptions}
                                        value={selectedCreatedBy}
                                        onChange={(event, newValue) => {
                                            setSelectedCreatedBy(newValue);
                                        }}
                                        onInputChange={(event, newInputValue) => {
                                            setUserSearchQuery(newInputValue);
                                        }}
                                        getOptionLabel={(option) => {
                                            if (typeof option === 'string') return option;
                                            return option?.full_name || '';
                                        }}
                                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                        loading={loadingUsers}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder="Created By"
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                            />
                                        )}
                                        renderOption={(props, option) => (
                                            <Box component="li" {...props} key={option.id}>
                                                {option.full_name} ({option.id})
                                            </Box>
                                        )}
                                        noOptionsText={debouncedUserSearchQuery.length < 2 ? "Type at least 2 characters" : "No users found"}
                                    />
                                    <TextField
                                        size="small"
                                        type="date"
                                        label="Date"
                                        value={selectedDate}
                                        onChange={(e) => setSelectedDate(e.target.value)}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        sx={{ minWidth: 150 }}
                                    />
                                </Stack>
                            }
                            fetchData={(nextQueryURL) => fetchOpdInvoiceData(nextQueryURL)}
                            variant="invoices"
                        />
                    </>
                )}
            </Box>

            <AbhaHipLinker
                open={openHipLinker}
                handleClose={() => {
                    setOpenHipLinker(false);
                    setHipLinkPatient(null);
                    setHipLinkContexts([]);
                }}
                patient={hipLinkPatient}
                careContexts={hipLinkContexts}
                onSuccess={(linkedRefs = []) => {
                    if (Array.isArray(linkedRefs) && linkedRefs.length > 0) {
                        setPharmacyInvoices((prev) =>
                            prev.map((inv) =>
                                linkedRefs.includes(invoiceCareContextRef(inv.invoice_number))
                                    ? { ...inv, abha_linked: true, abha_link_status: 'LINKED' }
                                    : inv
                            )
                        );
                        setLabInvoices((prev) =>
                            prev.map((inv) =>
                                linkedRefs.includes(invoiceCareContextRef(inv.invoice_number))
                                    ? { ...inv, abha_linked: true, abha_link_status: 'LINKED' }
                                    : inv
                            )
                        );
                        setOpdInvoices((prev) =>
                            prev.map((inv) =>
                                linkedRefs.includes(String(inv.invoice_number))
                                    ? { ...inv, abha_linked: true, abha_link_status: 'LINKED' }
                                    : inv
                            )
                        );
                    }
                    if (selectedTab === 0) {
                        fetchPharmacyInvoiceData();
                    } else if (selectedTab === 1) {
                        fetchLabInvoiceData();
                    } else {
                        fetchOpdInvoiceData();
                    }
                }}
            />

        </Box>
    );
}

